import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import { useSelector } from 'react-redux';
import once from 'lodash/once';

import { HELP_TABS } from 'xm-constants/helpView';
import { showHelp as showHelpAction } from 'actions/helpPopover';
import { getHasQuickStartGuide } from 'selectors/helpView';
import { getLoggedInUserId } from 'selectors/loggedInUser';
import useAction from 'hooks/useAction';
import { useDocumentBinding } from '@xmatters/react';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { useShowQuickStartOnLogin } from './QuickStartGuide/hooks';

import {
  clampGeometryToViewport,
  HelpGeometryContext,
  getStoredHelpGeometry,
  storeHelpGeometry,
  triggerRelayouts,
} from './utils';

export function useAutomaticQuickStartOpening() {
  const hasQuickStart = useSelector(getHasQuickStartGuide);
  const loggedInUserId = useSelector(getLoggedInUserId);
  const showHelp = useAction(showHelpAction);
  const [showQuickStartOnLogin] = useShowQuickStartOnLogin();
  // const isHelpDrawerOpen = useSelector(getIsHelpDrawerOpen);
  const isHelpDrawerOpen = useSelector(state => state.helpPopover.isOpen);
  // We only want to open on init, and only want to do it once:
  const isHelpDrawerOpenRef = useRef();
  isHelpDrawerOpenRef.current = isHelpDrawerOpen;

  // Intentional useState instead of useCallback to avoid having to keep double building fns ("once" + initial fn):
  const [openQuickStartGuideAtStart] = useState(() =>
    once(() => {
      if (!isHelpDrawerOpenRef.current) {
        showHelp(null, { tab: HELP_TABS.QUICK_START });
      }
    }),
  );

  useEffect(() => {
    /**
     * openQuickStartGuideAtStart will only run ONCE and won't do anything if
     * the Help Drawer is already open. As such it can be called even when toggling
     * the showQuickStartOnLogin flag.
     *
     * This also (intentionally) means that if the Help Drawer is open by default when loading
     * a new page (e.g. navigating to Cocoon), that takes priority over the Quick Start Guide.
     */
    if (loggedInUserId && hasQuickStart && showQuickStartOnLogin) {
      openQuickStartGuideAtStart();
    }
  }, [
    loggedInUserId,
    hasQuickStart,
    showQuickStartOnLogin,
    openQuickStartGuideAtStart,
  ]);
}

export function useHelpGeometryContextProviderValue() {
  const [isHelpDrawerDocked, setIsHelpDrawerDocked] = useLocalStorage(
    'xm_isHelpMenuDocked',
    true,
  );
  const [geometry, setGeometry] = useState(() => getStoredHelpGeometry());
  const geometryRef = useRef();
  geometryRef.current = geometry;

  useEffect(() => {
    triggerRelayouts();
  }, [isHelpDrawerDocked]);

  return useMemo(
    () => ({
      isHelpDrawerDocked,
      setIsHelpDrawerDocked,
      geometry,
      geometryRef,
      setGeometry,
    }),
    [
      isHelpDrawerDocked,
      setIsHelpDrawerDocked,
      geometry,
      geometryRef,
      setGeometry,
    ],
  );
}

// Provides all items from useHelpGeometryContextProviderValue;
export const useHelpGeometryContext = () => useContext(HelpGeometryContext);

export function useHelpViewDragPositioning() {
  const { isHelpDrawerDocked, geometryRef, setGeometry } =
    useHelpGeometryContext();
  const [draggingRelativeCoords, setDraggingRelativeCoords] = useState(null);
  const isDragging = !!draggingRelativeCoords;

  useDocumentBinding(
    'mousemove',
    e => {
      if (draggingRelativeCoords && !isHelpDrawerDocked) {
        setGeometry({
          ...geometryRef.current,
          x: e.clientX - draggingRelativeCoords.x,
          y: e.clientY - draggingRelativeCoords.y,
        });
      }
    },
    [isHelpDrawerDocked, geometryRef, draggingRelativeCoords, setGeometry],
  );

  useDocumentBinding(
    'mouseup',
    () => {
      if (isDragging && !isHelpDrawerDocked) {
        setDraggingRelativeCoords(null);
        setGeometry(clampGeometryToViewport(geometryRef.current));
      }
    },
    [geometryRef, isDragging, setDraggingRelativeCoords, setGeometry],
  );

  const onMouseUp = useCallback(() => {
    setDraggingRelativeCoords(null);
    storeHelpGeometry(geometryRef.current);
  }, [geometryRef, setDraggingRelativeCoords]);

  const onMouseDown = useCallback(
    e => {
      setDraggingRelativeCoords({
        x: e.clientX - geometryRef.current.x,
        y: e.clientY - geometryRef.current.y,
      });
    },
    [geometryRef, setDraggingRelativeCoords],
  );

  return {
    onMouseUp: isHelpDrawerDocked ? undefined : onMouseUp,
    onMouseDown: isHelpDrawerDocked ? undefined : onMouseDown,
  };
}
